import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Scrollbars } from 'react-custom-scrollbars';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ReactDOMServer from 'react-dom/server';
import {
  AppDatePicker,
  AppDeleteModal,
  AppFileUploader,
  AppInput,
  AppInputNumber,
  AppModal,
  AppMultiSelect,
  AppNoInfo,
  AppNotificationAlert,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from '@/layout/AppComponents';
import { axiosPath } from '../../../../../../routing/helpers/router';
import DebugMultiPressAlert from '../../../../../components/debug/DebugMultiPressAlert';
import * as currentPageAction from '../../../../../../store/actions/currentPageAction';
import useIsMounted from '../../../../../components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import Translator from '../../../../../components/helpers/Translator';
import minions from '../../../../../../minions/minions';
import colors from '../../../../../../constants/colors';
import { toFormData } from '../../../../../../minions/forms';
import EditorTranslatable from '../../../../../components/form/Texts/EditorTranslatable';

export default function InformationTab({ elementID }) {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const global = useSelector((state) => state.global);
  const userBridge = useSelector((state) => state.module[moduleBridgeHex].userBridge);
  const elementLevels = useSelector((state) => state.currentPage?.elementLevels);
  const resLevel1 = useSelector(
    (state) => state.currentPage?.elementLevels?.level1?.[elementID],
  );
  const resLevel2 = useSelector(
    (state) => state.currentPage?.elementLevels?.level2?.[elementID],
  );
  const information = useSelector((s) => s.currentPage?.informationTab?.[elementID]);
  const [isLoading, setIsLoading] = useState(true);
  const [initialValues, setInitialValues] = useState();
  const [validationSchema, setValidationSchema] = useState();
  const [editMode, setEditMode] = useState(false);
  const [archiveModal, setArchiveModal] = useState();
  const [deleteModal, setDeleteModal] = useState();

  // > LOAD INFORMATION TAB
  const loadInformation = useCallback(
    async () =>
      axios
        .post(axiosPath('api_r_user_module_resourceManager_information_tab'), {
          moduleBridgeHex,
          resourceID: elementID,
        })
        .then(({ data }) => {
          if (isMounted.current) {
            const iv = {
              name: resLevel1?.name,
              description: data?.description,
              plant: resLevel2?.plant?.plantID,
              files: [],
              deletedFiles: [],
            };

            const schema = {
              name: Yup.string().required(
                <Translator file="client" name="client.please-enter-name" />,
              ),
            };

            data?.features?.forEach((f) => {
              iv[`feature${f?.hex}`] = f?.value;

              switch (f?.type) {
                case 0:
                case 1:
                  if (f?.quantity > 0) {
                    schema[`feature${f?.hex}`] = Yup.string().max(
                      f?.quantity,
                      ReactDOMServer.renderToString(
                        <Translator
                          file="module-resourceManager"
                          name="resource.maximum.quantity.characters.permitted"
                          variables={{
                            quantity: f?.quantity,
                          }}
                        />,
                      ),
                    );
                  }
                  break;
                case 4:
                  schema[`feature${f?.hex}`] = Yup.number().nullable();
                  break;
                case 5:
                  schema[`feature${f?.hex}`] = Yup.array()
                    .of(
                      Yup.number().typeError(
                        ReactDOMServer.renderToString(
                          <Translator
                            file="module-resourceManager"
                            name="resource.each.element.must.be.number"
                          />,
                        ),
                      ),
                    )
                    .notRequired();
                  break;
                default:
                  break;
              }
            });

            setInitialValues(iv);
            setValidationSchema(Yup.lazy(() => Yup.object().shape(schema)));

            dispatch(
              currentPageAction.change(
                'informationTab',
                {
                  [elementID]: data,
                },
                true,
              ),
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setIsLoading(false)),
    [moduleBridgeHex, elementID, resLevel1, resLevel2],
  );

  useEffect(() => {
    if (isLoading) loadInformation();
  }, [isLoading]);
  // < LOAD INFORMATION TAB

  // > SAVE
  const handleEdit = useCallback(
    async (values) =>
      axios
        .post(
          axiosPath('api_r_user_module_resourceManager_resource_information_edit'),
          toFormData({
            moduleBridgeHex,
            resourceID: elementID,
            ...values,
          }),
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        )
        .then(({ data }) => {
          if (isMounted.current) {
            const updatedElementLevels = {
              ...elementLevels,
              level1: {
                ...(elementLevels?.level1 ?? {}),
                [elementID]: data?.level1,
              },
              level2: {
                ...(elementLevels?.level2 ?? {}),
                [elementID]: data?.level2,
              },
            };
            dispatch(currentPageAction.change('elementLevels', updatedElementLevels));
            setIsLoading(false);
            setEditMode(false);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, elementID],
  );
  // < SAVE

  // > REDIRECT TO PLANT WITH OBLIGATION FILTER
  const createFilterAndRedirect = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            'api_r_user_module_resourceManager_generateFilterHexCodeWithObligationIDs',
          ),
          {
            moduleBridgeHex: resLevel2?.plant?.moduleBridgeHex,
            resourceID: resLevel1?.id,
          },
        )
        .then(({ data }) => {
          if (data?.hexCode) {
            window.open(
              `${global.urlData.rootProject}r/legal-compliance/${resLevel2?.plant?.moduleBridgeHex}/evaluation/${resLevel2?.plant?.plantHex}/1/${data?.hexCode}/`,
              '_blank',
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [global, resLevel1, resLevel2],
  );
  // < REDIRECT TO PLANT WITH OBLIGATION FILTER

  const handleDownloadFile = useCallback(
    async (key) => {
      axios
        .post(
          axiosPath('api_r_user_module_resourceManager_information_file_download'),
          {
            moduleBridgeHex,
            resourceID: resLevel1?.id,
            key,
          },
        )
        .then(({ data }) => {
          if (isMounted.current) {
            window.open(data, '_blank');
          }
        })
        .catch((e) => {
          if (e?.response?.status === 404) {
            AppNotificationAlert({
              title: ReactDOMServer.renderToString(
                <Translator file="user" name="user.error" />,
              ),
              message: e?.response?.data,
              type: 'danger',
              duration: 5000,
            });
          } else {
            dispatch(errorHandler(e));
          }
        });
    },
    [moduleBridgeHex, resLevel1],
  );

  const handleArchive = useCallback(async () => {
    axios
      .post(
        axiosPath('api_r_user_module_resourceManager_resource_information_archive'),
        {
          moduleBridgeHex,
          resourceID: resLevel1?.id,
        },
      )
      .then(() => window.location.reload())
      .catch((e) => dispatch(errorHandler(e)));
  }, [moduleBridgeHex, resLevel1]);

  const handleDelete = useCallback(async () => {
    axios
      .post(
        axiosPath('api_r_user_module_resourceManager_resource_information_delete'),
        {
          moduleBridgeHex,
          resourceID: resLevel1?.id,
        },
      )
      .then(() => window.location.reload())
      .catch((e) => dispatch(errorHandler(e)));
  }, [moduleBridgeHex, resLevel1]);

  const renderFeatureInputs = useCallback(
    (values, errors, setFieldValue) =>
      information?.features?.map((f) => {
        switch (f?.type) {
          case 0:
            return (
              <AppInput
                title={f?.name}
                value={values?.[`feature${f?.hex}`]}
                error={errors?.[`feature${f?.hex}`]}
                onChange={(v) => setFieldValue(`feature${f?.hex}`, v)}
                isClearable
              />
            );
          case 1:
            return (
              <AppTextArea
                title={f?.name}
                value={values?.[`feature${f?.hex}`]}
                error={errors?.[`feature${f?.hex}`]}
                onChange={(v) => setFieldValue(`feature${f?.hex}`, v)}
                isClearable
              />
            );
          case 2:
            return (
              <AppInputNumber
                title={f?.name}
                value={values?.[`feature${f?.hex}`]}
                error={errors?.[`feature${f?.hex}`]}
                onChange={(v) => setFieldValue(`feature${f?.hex}`, v)}
                decimals={f?.quantity > 0 ? f?.quantity : 99}
                isClearable
              />
            );
          case 3:
            return (
              <AppDatePicker
                title={f?.name}
                value={values?.[`feature${f?.hex}`]}
                error={errors?.[`feature${f?.hex}`]}
                onChange={(v) => setFieldValue(`feature${f?.hex}`, v)}
                isClearable
                hasToggleType
              />
            );
          case 4:
            return (
              <AppSingleSelect
                title={f?.name}
                options={f?.options}
                value={values?.[`feature${f?.hex}`]}
                error={errors?.[`feature${f?.hex}`]}
                onChange={(opt) => setFieldValue(`feature${f?.hex}`, opt)}
                isClearable
                hasSearchBox
              />
            );
          case 5:
            return (
              <AppMultiSelect
                title={f?.name}
                options={f?.options}
                value={values?.[`feature${f?.hex}`]}
                error={errors?.[`feature${f?.hex}`]}
                onChange={(opt) => setFieldValue(`feature${f?.hex}`, opt)}
                hasSearchBox
                hasSelectOptions
              />
            );
          default:
            return null;
        }
      }),
    [information],
  );

  const toggleDeleteFile = (values, setFieldValue, salt) => {
    const updateDeletedFiles = values.deletedFiles;

    if (updateDeletedFiles.includes(salt)) {
      setFieldValue(
        'deletedFiles',
        updateDeletedFiles.filter((s) => s !== salt),
      );
    } else {
      updateDeletedFiles.push(salt);
      setFieldValue('deletedFiles', updateDeletedFiles);
    }
  };

  if (isLoading) return <AppNoInfo minHeight={300} />;

  return (
    <div>
      {editMode ? (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleEdit}
        >
          {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <div className="row mb-5">
                <div className="col-xs-12 col-lg-6">
                  <AppInput
                    title={
                      <Translator
                        file="module-resourceManager"
                        name="resource.name"
                      />
                    }
                    onChange={(value) => setFieldValue('name', value)}
                    value={values.name}
                    error={errors.name}
                    isClearable
                  />
                  <EditorTranslatable
                    title={
                      <Translator
                        file="module-resourceManager"
                        name="resource.description"
                      />
                    }
                    icon={<AppRemixIcon icon="file-text" />}
                    onChange={(opt) => setFieldValue('description', opt)}
                    value={values.description}
                    className=""
                    toolbar={{
                      toolbar: [
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ color: [] }, { background: [] }],
                        ['clean'],
                        [{ direction: 'rtl' }],
                      ],
                    }}
                    height={200}
                  />
                  <AppSingleSelect
                    placeholder={
                      <Translator
                        file="module-resourceManager"
                        name="resource.select.installation"
                      />
                    }
                    options={information?.plants}
                    icon={<AppRemixIcon icon="building-4" />}
                    onChange={(value) => setFieldValue('plant', value)}
                    value={values.plant}
                    error={errors.plant}
                    hasSearchBox
                    isClearable
                  />
                  <AppFileUploader
                    isMulti
                    placeholder={
                      <Translator
                        file="module-resourceManager"
                        name="resource.select.one.or.more.files"
                      />
                    }
                    onChange={(opt) =>
                      setFieldValue('files', [...values?.files, ...opt])
                    }
                  />
                  <FileList uploaded>
                    {information?.files
                      ?.filter((f) => f?.salt)
                      ?.map((f) => (
                        <li
                          key={f?.salt}
                          title={
                            values.deletedFiles.includes(f?.salt)
                              ? ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-legalCompliance"
                                    name="click.restore"
                                  />,
                                )
                              : ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-legalCompliance"
                                    name="click.delete"
                                  />,
                                )
                          }
                          onClick={() =>
                            toggleDeleteFile(values, setFieldValue, f?.salt)
                          }
                        >
                          <AppRemixIcon
                            className="pointer mr-3"
                            icon="delete-bin"
                            color={
                              values.deletedFiles.includes(f?.salt)
                                ? colors.grey400
                                : colors.danger
                            }
                          />
                          {minions.files.fileIcon(
                            f?.name,
                            true,
                            values.deletedFiles.includes(f?.salt)
                              ? colors.grey400
                              : null,
                            'mr-2',
                          )}
                          <FileName isRemoved={values.deletedFiles.includes(f?.salt)}>
                            {f?.name}
                          </FileName>
                        </li>
                      ))}
                  </FileList>
                  <FileList>
                    {values?.files
                      ?.filter((f) => !f?.salt)
                      ?.map((f, i) => (
                        <li
                          key={i}
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="click.delete"
                            />,
                          )}
                          onClick={() =>
                            setFieldValue(
                              'files',
                              values?.files?.filter((_, index) => index !== i),
                            )
                          }
                        >
                          <AppRemixIcon
                            className="pointer mr-3"
                            icon="delete-bin"
                            color={colors.danger}
                          />
                          {minions.files.fileIcon(f?.name, true, null, 'mr-2')}
                          <FileName>{f?.name}</FileName>
                        </li>
                      ))}
                  </FileList>
                </div>
                <div className="col-xs-12 col-lg-6">
                  {renderFeatureInputs(values, errors, setFieldValue)}
                </div>
              </div>
              <div className="d-flex justify-content-end mt-3">
                <button
                  type="button"
                  className="btn btn-xs btn-outline-light mr-2"
                  onClick={() => setEditMode(false)}
                >
                  <Translator file="module-resourceManager" name="resource.cancel" />
                </button>
                <button
                  type="submit"
                  className="btn btn-xs btn-success"
                  disabled={isSubmitting}
                >
                  <Translator file="module-resourceManager" name="resource.edit" />
                </button>
              </div>
            </form>
          )}
        </Formik>
      ) : (
        <Container>
          <div className="pr-2">
            <h5 className="mb-2">{resLevel1?.name}</h5>
            {resLevel2?.plant && (
              <h6 className="mt-2 pointer" onClick={createFilterAndRedirect}>
                <b>Instalación:</b> {resLevel2?.plant?.plantName}
                <AppRemixIcon className="ml-1" icon="external-link" />
              </h6>
            )}
            {information?.files?.map((file) => (
              <span
                className="pointer"
                title={file?.name}
                key={file?.salt}
                onClick={() => handleDownloadFile(file?.salt)}
              >
                {minions.files.fileIcon(file?.name, true, null, 'mr-2')}
              </span>
            ))}
            <Scrollbars style={{ height: '385px', paddingRight: '10px' }}>
              <div
                className="mt-2"
                dangerouslySetInnerHTML={{
                  __html: resLevel1?.description,
                }}
              />
            </Scrollbars>
          </div>
          <InformationRight>
            <Scrollbars style={{ height: '385px', paddingRight: '10px' }}>
              {(![2, 3].includes(userBridge?.permissions?.level) ||
                (userBridge?.permissions?.level === 3 &&
                  userBridge?.permissions?.resourceEditting)) && (
                <div className="d-flex justify-content-end">
                  <span
                    className="pointer"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-resourceManager"
                        name="resource.edit"
                      />,
                    )}
                    onClick={() => setEditMode(true)}
                  >
                    <AppRemixIcon icon="edit-box" />
                  </span>
                  {(userBridge?.permissions?.level === 0 ||
                    (userBridge?.permissions?.level === 3 &&
                      userBridge?.permissions?.resourceEditting)) &&
                    !information?.isArchived && (
                      <span
                        className="pointer ml-3"
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-resourceManager"
                            name="resource.archive"
                          />,
                        )}
                        onClick={() => setArchiveModal(elementID)}
                      >
                        <AppRemixIcon icon="inbox-archive" color={colors.danger} />
                      </span>
                    )}

                  {(userBridge?.permissions?.level === 0 ||
                    (userBridge?.permissions?.level === 3 &&
                      userBridge?.permissions?.resourceEditting)) &&
                    information?.isArchived && (
                      <span
                        className="pointer ml-3"
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-resourceManager"
                            name="resource.unarchive"
                          />,
                        )}
                        onClick={() => setArchiveModal(elementID)}
                      >
                        <AppRemixIcon icon="inbox-archive" color={colors.success} />
                      </span>
                    )}
                  {(userBridge?.permissions?.level === 0 ||
                    (userBridge?.permissions?.level === 3 &&
                      userBridge?.permissions?.resourceEditting)) &&
                    information?.isArchived && (
                      <span
                        className="pointer ml-3"
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-resourceManager"
                            name="Eliminar definitivamente"
                          />,
                        )}
                        onClick={() => setDeleteModal(elementID)}
                      >
                        <AppRemixIcon icon="delete-bin-6" color={colors.danger} />
                      </span>
                    )}
                </div>
              )}
              <DebugMultiPressAlert info={`ID: ${resLevel1?.id}`}>
                <h4>
                  <b>CODE: </b>
                  {resLevel1?.hex || '------'}
                </h4>
              </DebugMultiPressAlert>
              {information?.features?.length > 0 && (
                <>
                  <h4>
                    <b>
                      <Translator
                        file="module-resourceManager"
                        name="resource.specifications"
                      />
                    </b>
                  </h4>
                  <ul>
                    {information?.features?.map((f) => (
                      <li key={f?.hex}>
                        <h4>
                          <b>{f?.name}:</b>
                        </h4>
                        {Array.isArray(f?.value) ? (
                          <>
                            {f?.value?.map((v) => (
                              <h4>
                                {f?.options?.find((o) => o?.value === v)?.label}
                              </h4>
                            ))}
                          </>
                        ) : (
                          <h4>
                            {f?.type === 4
                              ? f?.options?.find((o) => o?.value === f?.value)?.label
                              : f?.value}
                          </h4>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </Scrollbars>
          </InformationRight>
          <AppModal
            show={userBridge?.permissions?.level === 0 && archiveModal === elementID}
            title={
              <Translator
                file="module-resourceManager"
                name="resource.eliminate.relationship"
              />
            }
            onClose={() => setArchiveModal(undefined)}
          >
            {information?.isArchived ? (
              <div>
                <Translator
                  file="module-resourceManager"
                  name="resource.want.unarchive.asset"
                />
              </div>
            ) : (
              <div>
                <Translator
                  file="module-resourceManager"
                  name="resource.want.archive.asset"
                />
              </div>
            )}
            <div className="d-flex justify-content-end mt-3">
              <button
                type="button"
                className="btn btn-xs btn-outline-light mr-2"
                onClick={() => setArchiveModal(undefined)}
              >
                <Translator file="module-resourceManager" name="resource.cancel" />
              </button>
              <button
                type="button"
                className={`btn btn-xs btn-${
                  information?.isArchived ? 'success' : 'danger'
                }`}
                onClick={handleArchive}
              >
                <Translator
                  file="module-resourceManager"
                  name={
                    information?.isArchived
                      ? 'resource.unarchive'
                      : 'resource.archive'
                  }
                />
              </button>
            </div>
          </AppModal>
          <AppDeleteModal
            show={userBridge?.permissions?.level === 0 && deleteModal === elementID}
            title={
              <Translator
                file="module-resourceManager"
                name="resource.delete.asset"
              />
            }
            onClose={() => setDeleteModal(undefined)}
            onConfirm={handleDelete}
            sensitive
            sensitiveWord={
              <Translator file="module-resourceManager" name="resource.delete" />
            }
          />
        </Container>
      )}
    </div>
  );
}

const Container = styled.div`
  display: grid;
  grid-template-columns: 0.6fr 0.4fr;
  height: 400px;
`;

const InformationRight = styled.div`
  border-left: 1px solid #ddd;
  padding: 10px;
  h3 {
    font-size: 12px;
    font-weight: bold;
  }
  h4 {
    font-size: 10px;
    text-transform: uppercase;
  }
`;

const FileList = styled.ul`
  list-style: none;
  margin: 10px 0 0 0;
  padding-left: 6px;
  background-color: ${(props) => (props.uploaded ? colors.grey200 : 'initial')};
  li {
    cursor: pointer;
    display: flex;
    align-items: center;
    border-top: 1px solid ${colors.grey300};
    padding: 8px 0;
    span {
      font-size: 12px;
      color: ${colors.primary};
    }
  }
  li:first-child {
    border-top: 0;
  }
`;
const FileName = styled.span`
  text-decoration: ${(props) => (props.isRemoved ? 'line-through' : 'unset')};
`;
